import classNames from 'classnames'
import { useMemo, useRef } from 'react'
import { useControlField, useField } from 'remix-validated-form'
import Combo from 'app/components/ui/combo/combo'
import FormInputWrapper, { type FormInputWrapperProps } from './FormInputWrapper'
import { OptionType } from 'types/OptionType'

export type FormSelectOption = { label: string; value: string | number }

// Options above this count get an in-dropdown search box; shorter lists don't
// (a search field on a 2-item dropdown is noise). Tunable per design (TRK-5214).
const SEARCH_THRESHOLD = 8

// Synthetic change payload shaped like a native <select> ChangeEvent's relevant
// bits, so legacy handlers written as `(e) => e.target.value` or
// `e.currentTarget.value` keep working after migrating off the native select.
export type FormComboSelectChange = {
  target: { name: string; value: string }
  currentTarget: { name: string; value: string }
}

export type FormComboSelectProps = {
  options?: FormSelectOption[]
  labelClassName?: string
  className?: string
  inputClassName?: string
  placeholder?: string
  icon?: React.ReactElement
  /**
   * Force the in-dropdown search box on/off. When omitted, search is shown only
   * when the option count exceeds SEARCH_THRESHOLD.
   */
  showSearchInDropdown?: boolean
  /**
   * Receives a synthetic event `{ target: { name, value } }` for drop-in
   * compatibility with the legacy native-select `onChange={(e) => e.target.value}`.
   */
  onChange?: (event: FormComboSelectChange) => void
} & Omit<FormInputWrapperProps<HTMLSelectElement>, 'onChange'>

/**
 * Drop-in replacement for the legacy native-`<select>` `FormSelect` that renders
 * the same custom, searchable option list as the Workspace combobox
 * (`ComboBoxComponent`) instead of the browser's native dropdown.
 *
 * Unlike `FormComboAsync`, this submits a plain **string** value (the selected
 * option's `value`) so existing `z.string()` validation schemas keep working —
 * no schema changes required at call sites.
 */
export default function FormComboSelect({
  name,
  label,
  cols,
  wrapperClassName,
  labelClassName,
  className,
  inputClassName,
  placeholder,
  error: errorProp,
  disabled,
  newLineError,
  showErrorIcon,
  infoTooltip,
  isLocked,
  showSearchInDropdown,
  defaultValue,
  value: valueProp,
  icon,
  onChange,
  options,
}: FormComboSelectProps) {
  const {
    error: fieldError,
    defaultValue: fieldDefaultValue,
    setTouched,
  } = useField(name, {
    // Match the legacy FormSelect/FormInput behavior: only validate on submit,
    // not mid-interaction. Avoids surfacing errors while the user is still
    // choosing an option.
    validationBehavior: { initial: `onSubmit`, whenTouched: `onSubmit`, whenSubmitted: `onSubmit` },
  })
  const [controlValue, setValue] = useControlField<string | undefined>(name)
  const displayError = fieldError ?? errorProp
  const hiddenInputRef = useRef<HTMLInputElement>(null)

  // Map {label,value} form options to the OptionType shape ComboBoxComponent uses.
  const comboOptions = useMemo<OptionType[]>(
    () =>
      (options ?? []).map((opt) => ({
        id: String(opt.value),
        value: String(opt.value),
        name: opt.label,
      })),
    [options],
  )

  // Resolve the currently-selected string into an OptionType for the combobox.
  // Priority: the caller's controlled `value` prop (legacy native <select>
  // forwarded this straight onto the element, so controlled call sites like the
  // ops search tool and MarkPatientModal depend on it — TRK-5214-2), then
  // remix-validated-form's control-field value, then the field's form default,
  // then the explicit defaultValue prop.
  const selectedRaw =
    (valueProp as string | undefined) ??
    controlValue ??
    (fieldDefaultValue as string | undefined) ??
    (defaultValue as string | undefined)
  const selectedOption = useMemo<OptionType | undefined>(
    () => comboOptions.find((opt) => opt.value === selectedRaw),
    [comboOptions, selectedRaw],
  )

  const resolvedShowSearch = showSearchInDropdown ?? comboOptions.length > SEARCH_THRESHOLD

  return (
    <FormInputWrapper
      {...{
        name,
        label,
        error: displayError,
        newLineError,
        showErrorIcon,
        cols,
        wrapperClassName,
        className: labelClassName ?? `text-ui-sm`,
        infoTooltip,
        isLocked,
      }}
    >
      {/* Mirrors the selected value into a real hidden input so forms that detect
          dirty state by listening for the native `input` event on the form
          element (see useLocationSettingsGeneral) still fire. React-driven value
          changes don't emit a native input event, so onChange dispatches one
          manually below. Submits a plain string so z.string() schemas keep working. */}
      <input ref={hiddenInputRef} type="hidden" name={name} value={selectedRaw ?? ``} readOnly />
      <div className="relative">
        {icon && (
          <div className="pointer-events-none absolute inset-y-0 left-2 z-10 flex w-6 items-center justify-center text-body-sm">
            {icon}
          </div>
        )}
        <Combo
          name={undefined}
          className={className}
          inputClassName={classNames(inputClassName, icon && `pl-10! text-body-sm text-strong`)}
          placeholder={placeholder}
          options={comboOptions}
          value={selectedOption}
          disabled={disabled}
          showSearchInDropdown={resolvedShowSearch}
          useSingleFieldInput={false}
          onChange={(opt) => {
            const next = (opt?.value as string | undefined) ?? ``
            // Register the selection with remix-validated-form (submission +
            // touched tracking).
            setValue(next)
            setTouched(true)
            // Emit a bubbling native `input` event from the hidden field so
            // form-level `input` listeners (dirty detection) pick up the change.
            // Note: synthetic events can't set isTrusted=true, but no form
            // listener in this codebase checks it (verified for the dirty-state
            // consumers), and remix-validated-form's touched tracking above is
            // the primary path.
            hiddenInputRef.current?.dispatchEvent(new Event(`input`, { bubbles: true }))
            const field = { name, value: next }
            onChange?.({ target: field, currentTarget: field })
          }}
        />
      </div>
    </FormInputWrapper>
  )
}

// Named alias for call sites migrating off the shadcn `ui/form-select` named export.
export { FormComboSelect as FormSelect }
